import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { TableColumnHighlight } from '../../models/table.model';
import { TableCellBaseComponent } from '../table-cell-base/table-cell-base.component';

@Component({
  standalone: true,
  imports: [AsyncPipe, NgIf, TableCellBaseComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tsq-table-cell-highlight',
  template: `
    <tsq-table-cell-base size="small">
      <div
        *ngIf="showHighlight$ | async"
        class="size-8 rounded-full"
        style="background-color: #0086ea;"
      ></div>
    </tsq-table-cell-base>
  `,
  styles: [
    `
      :host {
        @apply min-w-0;
      }
    `,
  ],
})
export class TableCellHighlightComponent<TRowData> {
  private readonly contentSubject = new BehaviorSubject<
    TableColumnHighlight<TRowData>['content'] | undefined
  >(undefined);
  private readonly rowDataSubject = new BehaviorSubject<TRowData | undefined>(undefined);

  protected readonly showHighlight$ = combineLatest([
    this.contentSubject.asObservable(),
    this.rowDataSubject.asObservable(),
  ]).pipe(
    map(([content, rowData]) => {
      if (!content || !rowData) {
        return false;
      }

      if (typeof content.show === 'boolean') {
        return content.show;
      } else if (typeof content.show === 'function') {
        return content.show(rowData) ?? false;
      } else if ('key' in content.show) {
        const cellData = rowData[content.show.key];

        return content.show.transform(cellData) ?? false;
      }

      return false;
    }),
  );

  @Input()
  set content(value: TableColumnHighlight<TRowData>['content']) {
    this.contentSubject.next(value);
  }

  @Input()
  set rowData(value: TRowData) {
    this.rowDataSubject.next(value);
  }
}
